<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useVuelidate } from '@vuelidate/core';
import { required, email } from '@vuelidate/validators';
import Button from 'dashboard/components-next/button/Button.vue';
// GK-HUB: Inbox/team assignment on agent creation
import InboxMembersAPI from '../../../../api/inboxMembers';
import TeamsAPI from '../../../../api/teams';

const emit = defineEmits(['close']);

const store = useStore();
const { t } = useI18n();

const agentName = ref('');
const agentEmail = ref('');
const selectedRoleId = ref('agent');
// GK-HUB: Admin define senha na criação (ISPs sem SMTP)
const agentPassword = ref('');

// GK-HUB: Inbox and team selection
const selectedInboxes = ref([]);
const selectedTeams = ref([]);
const isSavingAssignments = ref(false);

// GK-HUB: Password validation (min 6, 1 upper, 1 lower, 1 digit, 1 special)
const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^a-zA-Z\d]).{6,}$/;
const validPassword = {
  $validator: v => !v || passwordRegex.test(v),
  $message: 'Invalid password',
};

const rules = {
  agentName: { required },
  agentEmail: { required, email },
  selectedRoleId: { required },
  agentPassword: { required, validPassword },
};

const v$ = useVuelidate(rules, {
  agentName,
  agentEmail,
  selectedRoleId,
  agentPassword,
});
// GK-HUB: end password validation

const uiFlags = useMapGetter('agents/getUIFlags');
const getCustomRoles = useMapGetter('customRole/getCustomRoles');
// GK-HUB: Inbox/team lists from stores
const inboxesList = useMapGetter('inboxes/getActiveInboxes'); // GK-HUB: excludes deactivated;
const teamsList = useMapGetter('teams/getTeams');

// GK-HUB: Added supervisor and manager roles to agent creation form
const roles = computed(() => {
  const defaultRoles = [
    {
      id: 'administrator',
      name: 'administrator',
      label: t('AGENT_MGMT.AGENT_TYPES.ADMINISTRATOR'),
    },
    {
      id: 'manager',
      name: 'manager',
      label: t('AGENT_MGMT.AGENT_TYPES.MANAGER'),
    },
    {
      id: 'supervisor',
      name: 'supervisor',
      label: t('AGENT_MGMT.AGENT_TYPES.SUPERVISOR'),
    },
    {
      id: 'agent',
      name: 'agent',
      label: t('AGENT_MGMT.AGENT_TYPES.AGENT'),
    },
  ];

  const customRoles = getCustomRoles.value.map(role => ({
    id: role.id,
    name: `custom_${role.id}`,
    label: role.name,
  }));

  return [...defaultRoles, ...customRoles];
});
// GK-HUB: end role list

const selectedRole = computed(() =>
  roles.value.find(
    role =>
      role.id === selectedRoleId.value || role.name === selectedRoleId.value
  )
);

// GK-HUB: Fetch inboxes/teams on mount
onMounted(() => {
  store.dispatch('inboxes/get');
  store.dispatch('teams/get');
});

// GK-HUB: Assign agent to selected inboxes and teams after creation
const assignInboxesAndTeams = async agentId => {
  const promises = [];

  // Add agent to each selected inbox
  selectedInboxes.value.forEach(inbox => {
    promises.push(
      InboxMembersAPI.addAgents({ inboxId: inbox.id, userIds: [agentId] })
    );
  });

  // Add agent to each selected team
  selectedTeams.value.forEach(team => {
    promises.push(
      // GK-HUB (#115): dialeto members[{ user_id }] — sem receives_assignment, o backend aplica o
      // default por role (agente→recebedor, não-agente→observador). Antes usava agentsList (chave morta
      // desde a v1.7.22) → payload vazio → sync_members não adicionava ninguém (no-op silencioso).
      TeamsAPI.addAgents({ teamId: team.id, members: [{ user_id: agentId }] })
    );
  });

  if (promises.length > 0) {
    await Promise.all(promises);
  }
};
// GK-HUB: end inbox/team assignment

const addAgent = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;

  try {
    const payload = {
      name: agentName.value,
      email: agentEmail.value,
      password: agentPassword.value, // GK-HUB: senha definida pelo admin
    };

    if (selectedRole.value.name.startsWith('custom_')) {
      payload.custom_role_id = selectedRole.value.id;
    } else {
      payload.role = selectedRole.value.name;
    }

    const createdAgent = await store.dispatch('agents/create', payload);

    // GK-HUB: Assign inboxes and teams after agent creation
    if (
      createdAgent?.id &&
      (selectedInboxes.value.length || selectedTeams.value.length)
    ) {
      isSavingAssignments.value = true;
      try {
        await assignInboxesAndTeams(createdAgent.id);
      } catch {
        useAlert(t('AGENT_MGMT.EDIT.ASSIGNMENTS_ERROR'));
      } finally {
        isSavingAssignments.value = false;
      }
    }
    // GK-HUB: end inbox/team assignment

    useAlert(t('AGENT_MGMT.ADD.API.SUCCESS_MESSAGE'));
    emit('close');
  } catch (error) {
    const {
      response: {
        data: {
          error: errorResponse = '',
          attributes: attributes = [],
          message: attrError = '',
        } = {},
      } = {},
    } = error;

    let errorMessage = '';
    if (error?.response?.status === 422 && !attributes.includes('base')) {
      errorMessage = t('AGENT_MGMT.ADD.API.EXIST_MESSAGE');
    } else {
      errorMessage = t('AGENT_MGMT.ADD.API.ERROR_MESSAGE');
    }
    useAlert(errorResponse || attrError || errorMessage);
  }
};
</script>

<template>
  <div class="flex flex-col h-auto overflow-auto">
    <woot-modal-header
      :header-title="$t('AGENT_MGMT.ADD.TITLE')"
      :header-content="$t('AGENT_MGMT.ADD.DESC')"
    />
    <form class="flex flex-col items-start w-full" @submit.prevent="addAgent">
      <div class="w-full">
        <label :class="{ error: v$.agentName.$error }">
          {{ $t('AGENT_MGMT.ADD.FORM.NAME.LABEL') }}
          <input
            v-model="agentName"
            type="text"
            :placeholder="$t('AGENT_MGMT.ADD.FORM.NAME.PLACEHOLDER')"
            @input="v$.agentName.$touch"
          />
        </label>
      </div>

      <div class="w-full">
        <label :class="{ error: v$.selectedRoleId.$error }">
          {{ $t('AGENT_MGMT.ADD.FORM.AGENT_TYPE.LABEL') }}
          <select v-model="selectedRoleId" @change="v$.selectedRoleId.$touch">
            <option v-for="role in roles" :key="role.id" :value="role.id">
              {{ role.label }}
            </option>
          </select>
          <span v-if="v$.selectedRoleId.$error" class="message">
            {{ $t('AGENT_MGMT.ADD.FORM.AGENT_TYPE.ERROR') }}
          </span>
        </label>
      </div>

      <div class="w-full">
        <label :class="{ error: v$.agentEmail.$error }">
          {{ $t('AGENT_MGMT.ADD.FORM.EMAIL.LABEL') }}
          <input
            v-model="agentEmail"
            type="email"
            :placeholder="$t('AGENT_MGMT.ADD.FORM.EMAIL.PLACEHOLDER')"
            @input="v$.agentEmail.$touch"
          />
        </label>
      </div>

      <!-- GK-HUB: Password field (ISPs sem SMTP) -->
      <div class="w-full">
        <label :class="{ error: v$.agentPassword.$error }">
          {{ $t('AGENT_MGMT.ADD.FORM.PASSWORD.LABEL') }}
          <input
            v-model="agentPassword"
            type="password"
            :placeholder="$t('AGENT_MGMT.ADD.FORM.PASSWORD.PLACEHOLDER')"
            @input="v$.agentPassword.$touch"
          />
          <span v-if="v$.agentPassword.$error" class="message">
            {{ $t('AGENT_MGMT.ADD.FORM.PASSWORD.ERROR') }}
          </span>
        </label>
      </div>
      <!-- GK-HUB: end password field -->

      <!-- GK-HUB: Inbox assignment on agent creation -->
      <div class="w-full">
        <label>
          {{ $t('AGENT_MGMT.ADD.INBOXES.LABEL') }}
          <multiselect
            v-model="selectedInboxes"
            :options="inboxesList"
            track-by="id"
            label="name"
            multiple
            :close-on-select="false"
            :clear-on-select="false"
            hide-selected
            :placeholder="$t('AGENT_MGMT.ADD.INBOXES.PLACEHOLDER')"
            :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
            :deselect-label="$t('FORMS.MULTISELECT.ENTER_TO_REMOVE')"
          />
          <p class="mt-0.5 mb-0 text-xs text-n-slate-10">
            {{ $t('AGENT_MGMT.ADD.INBOXES.HELP') }}
          </p>
        </label>
      </div>

      <!-- GK-HUB: Team assignment on agent creation -->
      <div class="w-full">
        <label>
          {{ $t('AGENT_MGMT.ADD.TEAMS.LABEL') }}
          <multiselect
            v-model="selectedTeams"
            :options="teamsList"
            track-by="id"
            label="name"
            multiple
            :close-on-select="false"
            :clear-on-select="false"
            hide-selected
            :placeholder="$t('AGENT_MGMT.ADD.TEAMS.PLACEHOLDER')"
            :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
            :deselect-label="$t('FORMS.MULTISELECT.ENTER_TO_REMOVE')"
          />
          <p class="mt-0.5 mb-0 text-xs text-n-slate-10">
            {{ $t('AGENT_MGMT.ADD.TEAMS.HELP') }}
          </p>
        </label>
      </div>
      <!-- GK-HUB: end inbox/team assignment -->

      <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
        <Button
          faded
          slate
          type="reset"
          :label="$t('AGENT_MGMT.ADD.CANCEL_BUTTON_TEXT')"
          @click.prevent="emit('close')"
        />
        <Button
          type="submit"
          :label="$t('AGENT_MGMT.ADD.FORM.SUBMIT')"
          :disabled="v$.$invalid || uiFlags.isCreating || isSavingAssignments"
          :is-loading="uiFlags.isCreating || isSavingAssignments"
        />
      </div>
    </form>
  </div>
</template>
